import { useCallback, useEffect, useRef } from 'react';
import { logout, refresh } from '@/domains/auth/store/authSlice';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import TokenService from '@/services/TokenService/TokenService';
import { useDispatch } from '@/store/hooks';

export const useSystemCheckPageActivity = () => {
  const dispatch = useDispatch();
  const token = TokenService.getTokenData();
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const systemCheckTokenActivityExpiration = useCallback(function ({ isActive }: { isActive: boolean }) {
    if (!isActive) return;

    const lastUserActivityTimeStamp = localStorage.getItem(LocalStorageVariables.LastUserActivityTimeStamp);
    const lastPageActivityTimeStamp = localStorage.getItem(LocalStorageVariables.LastPageActivityTimeStamp);
    const userInactivityPeriodMs = Number(localStorage.getItem(LocalStorageVariables.UserInactivityPeriod)) * 60 * 1000; //period in milliseconds

    const userActivityCondition = Boolean(
      lastUserActivityTimeStamp && new Date().getTime() - +lastUserActivityTimeStamp > userInactivityPeriodMs,
    );
    const pageActivityCondition = Boolean(
      lastPageActivityTimeStamp && new Date().getTime() - +lastPageActivityTimeStamp > userInactivityPeriodMs,
    );

    if (userActivityCondition || pageActivityCondition) {
      dispatch(logout());
      return;
    }

    if (TokenService.isTokenExpiringSoon()) {
      dispatch(refresh());
    }
  }, []);

  const updateActivityTimestamp = () => {
    localStorage.setItem(LocalStorageVariables.LastPageActivityTimeStamp, JSON.stringify(Date.now()));
  };

  const startTokenCheckInterval = () => {
    if (!intervalIdRef.current) {
      intervalIdRef.current = setInterval(() => {
        systemCheckTokenActivityExpiration({ isActive: true });
      }, 5000);
    }
  };

  const stopTokenCheckInterval = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  };

  const handleVisibilityChange = () => {
    const isActive = !document.hidden;

    if (isActive) {
      startTokenCheckInterval();
    } else {
      updateActivityTimestamp();
      stopTokenCheckInterval();
    }

    systemCheckTokenActivityExpiration({ isActive });
  };

  useEffect(() => {
    if (!token) return;

    handleVisibilityChange();

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopTokenCheckInterval();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, dispatch]);
};
